import { Badge } from "design-system/atoms/Badge"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { cn } from "design-system/utils"
import { TFunction } from "i18next"
import { Signal, SignalHigh, SignalLow, SignalMedium } from "lucide-react"
import { useTranslation } from "react-i18next"
import { AnomalySeverity, AnomalySeverityEnum } from "schema"

const getSeverityBadgeStyles = (severity: AnomalySeverity) => {
  switch (severity) {
    case AnomalySeverityEnum.Critical:
      return "bg-destructive/10 border-error text-error"
    case AnomalySeverityEnum.High:
      return "bg-error/10 border-testing-1 text-testing-1"
    case AnomalySeverityEnum.Medium:
      return "bg-warning/10 border-warning text-warning"
    case AnomalySeverityEnum.Low:
      return "bg-muted-foreground/10 border-muted-foreground text-muted-foreground"
    default:
      return "bg-muted-foreground/10 border-muted-foreground text-muted-foreground"
  }
}

function getBadgeCopy(severity: AnomalySeverity, t: TFunction) {
  switch (severity) {
    case AnomalySeverityEnum.Critical:
      return t("dashboard.signals.anomaly.severity.critical")
    case AnomalySeverityEnum.High:
      return t("dashboard.signals.anomaly.severity.high")
    case AnomalySeverityEnum.Medium:
      return t("dashboard.signals.anomaly.severity.medium")
    case AnomalySeverityEnum.Low:
      return t("dashboard.signals.anomaly.severity.low")
    default:
      return t("dashboard.signals.anomaly.severity.unknown")
  }
}

function getIcon(severity: AnomalySeverity) {
  switch (severity) {
    case AnomalySeverityEnum.Critical:
      return <Signal className="size-4" />
    case AnomalySeverityEnum.High:
      return <SignalHigh className="size-4" />
    case AnomalySeverityEnum.Medium:
      return <SignalMedium className="size-4" />
    case AnomalySeverityEnum.Low:
      return <SignalLow className="size-4" />
  }
}

type Props = {
  severity: AnomalySeverity
  noLabel?: boolean
}

export function SeverityBadge({ severity, noLabel }: Props) {
  const { t } = useTranslation()

  const content = (
    <Badge
      variant="outline"
      className={cn(
        "shrink-0 text-xs h-7",
        getSeverityBadgeStyles(severity),
        noLabel && "size-7 items-center justify-center p-0",
      )}
    >
      {!noLabel && <span>{getBadgeCopy(severity, t)}</span>}
      {getIcon(severity)}
    </Badge>
  )

  if (noLabel) {
    return (
      <Tooltip>
        <TooltipTrigger>{content}</TooltipTrigger>
        <TooltipContent>{getBadgeCopy(severity, t)}</TooltipContent>
      </Tooltip>
    )
  }

  return content
}
